import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { KeyIcon, PlusIcon } from '@heroicons/react/24/outline';
import ButtonComponent from '../customButton';
import ChipComponent from '../chips/ChipComponent/ChipComponent';
import { getSemanticVariant } from '../chips/ChipComponent/chipVariants';
import TableComponent from '../TableComponent';
import { renderTableCell } from '../../utils/commonfunctions';
import CreateTokenModal from './CreateTokenModal';
import ModalWrapper from '../ModalWrapper';
import { CommonFooter } from '../FormComponent';
import { listApiTokens, revokeApiToken, deleteApiToken } from '../../utils/api/api_token_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { ApiTokenStatus } from '../../enums/ApiTokenStatusEnum';
import moment from 'moment';
import styles from '../../styles/launchesTable/index.module.scss';

/** Map backend status (ACTIVE, REVOKED, EXPIRED) to display label and chip variant */
const getStatusDisplay = (status) => {
  if (status === ApiTokenStatus.ACTIVE) return { label: 'Active', variant: getSemanticVariant('success') };
  if (status === ApiTokenStatus.REVOKED) return { label: 'Revoked', variant: getSemanticVariant('neutral') };
  if (status === ApiTokenStatus.EXPIRED) return { label: 'Expired', variant: getSemanticVariant('warning') };
  return { label: status || 'Unknown', variant: 'default' };
};

const ApiTokensTab = () => {
  const { showSnackbar } = useSnackbar();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState(null);

  const refreshTokens = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchTokens = async () => {
      setLoading(true);
      try {
        const response = await listApiTokens();
        if (!isMounted) return;
        if (response.status === STATUS.SUCCESS) {
          setTokens(Array.isArray(response.data) ? response.data : []);
        } else {
          showSnackbar(response.error || 'Failed to load tokens', 'error');
        }
      } catch (err) {
        if (isMounted) showSnackbar(err.message || 'Failed to load tokens', 'error');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchTokens();
    return () => { isMounted = false; };
  }, [refreshKey]);

  const handleCreateSuccess = () => {
    refreshTokens();
  };

  const openRevokeConfirm = (token) => {
    setTokenToRevoke(token);
    setRevokeConfirmOpen(true);
  };

  const closeRevokeConfirm = () => {
    if (!revokingId) {
      setRevokeConfirmOpen(false);
      setTokenToRevoke(null);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!tokenToRevoke?.id) return;
    setRevokingId(tokenToRevoke.id);
    try {
      const response = await revokeApiToken(tokenToRevoke.id);
      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || 'Token revoked', 'success');
        refreshTokens();
        setRevokeConfirmOpen(false);
        setTokenToRevoke(null);
      } else {
        showSnackbar(response.error || 'Failed to revoke token', 'error');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to revoke token', 'error');
    } finally {
      setRevokingId(null);
    }
  };

  const openDeleteConfirm = (token) => {
    setTokenToDelete(token);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (!deletingId) {
      setDeleteConfirmOpen(false);
      setTokenToDelete(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tokenToDelete?.id) return;
    setDeletingId(tokenToDelete.id);
    try {
      const response = await deleteApiToken(tokenToDelete.id);
      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || 'Token deleted', 'success');
        refreshTokens();
        setDeleteConfirmOpen(false);
        setTokenToDelete(null);
      } else {
        showSnackbar(response.error || 'Failed to delete token', 'error');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete token', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 150,
        flex: 1,
        renderCell: (params) => renderTableCell(params.value),
      },
      {
        field: 'maskedKey',
        headerName: 'Key',
        minWidth: 160,
        flex: 1,
        renderCell: (params) => (
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              color: 'text.secondary',
              py: 0.5,
              px: 1,
              bgcolor: 'grey.100',
              borderRadius: 1,
            }}
          >
            {params.value || '••••••••••'}
          </Box>
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        minWidth: 130,
        renderCell: (params) => (
          <Box className={styles['date-time-cell']}>
            <Typography variant="body2" className={styles['date']}>
              {params.value ? moment(params.value).format('YYYY-MM-DD') : ''}
            </Typography>
            <Typography variant="caption" className={styles['time']}>
              {params.value ? moment(params.value).local().format('hh:mm A') : ''}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'expiresInDays',
        headerName: 'Expires In Days',
        minWidth: 150,
        renderCell: (params) => {
          const { status } = params.row;
          if (status === ApiTokenStatus.REVOKED) return <Typography variant="body2">Revoked</Typography>;
          const days = params.value;
          if (days === null || days === undefined) return null;
          if (days <= 0) return <Typography variant="body2">Expired</Typography>;
          return <Typography variant="body2">{days} days</Typography>;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 100,
        renderCell: (params) => {
          const display = getStatusDisplay(params.row.status);
          return (
            <ChipComponent variant={display.variant} size="small">
              {display.label}
            </ChipComponent>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 90,
        sortable: false,
        renderCell: (params) => {
          const { status, id } = params.row;
          if (status === ApiTokenStatus.ACTIVE) {
            return (
              <ButtonComponent
                variant="secondary"
                ctaText="Revoke"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openRevokeConfirm(params.row);
                }}
                disabled={revokingId === id}
                className="text-red-600 hover:bg-red-50"
              />
            );
          }
          if (status === ApiTokenStatus.REVOKED || status === ApiTokenStatus.EXPIRED) {
            return (
              <ButtonComponent
                variant="secondary"
                ctaText="Delete"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDeleteConfirm(params.row);
                }}
                disabled={deletingId === id}
                className="text-red-600 hover:bg-red-50"
              />
            );
          }
          return null;
        },
      },
    ],
    [revokingId, deletingId]
  );

  const existingTokenNames = useMemo(() => tokens.map((t) => t.name).filter(Boolean), [tokens]);

  const rows = useMemo(
    () =>
      tokens.map((t) => ({
        id: t.id,
        maskedKey: t.maskedKey,
        name: t.name,
        created_at: t.created_at,
        expiresInDays: t.expiresInDays,
        status: t.status,
      })),
    [tokens]
  );

  const customNoData = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
      <KeyIcon style={{ width: 48, height: 48, color: '#94a3b8', marginBottom: 8 }} />
      <Typography color="text.secondary">No API tokens yet</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Generate a token to use with MCP or other integrations
      </Typography>
      <Box sx={{ mt: 2 }}>
        <ButtonComponent
          variant="primary"
          ctaText="Generate New Token"
          onClick={() => setCreateModalOpen(true)}
          className="white bg-blue600"
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Manage tokens for external integrations like MCP clients. Use them to authenticate without a browser.
          </Typography>
        </Box>
        <ButtonComponent
          variant="primary"
          ctaText="Generate New Token"
          startIcon={<PlusIcon style={{ width: 18, height: 18 }} />}
          onClick={() => setCreateModalOpen(true)}
          className="white bg-blue600"
        />
      </Box>

      <TableComponent
        title="API Tokens"
        rows={rows}
        columns={columns}
        loading={loading}
        idField="id"
        isData={rows.length > 0 || loading}
        customNoData={customNoData}
        hideColumnsVisibilityButton
        disableFooter
        disablePagination
      />

      <CreateTokenModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        existingTokenNames={existingTokenNames}
      />

      <ModalWrapper
        isOpen={revokeConfirmOpen}
        onClose={closeRevokeConfirm}
        title="Revoke API token"
        size="small"
        closeOnBackdrop={false}
        footer={
          <CommonFooter
            buttons={[
              {
                label: 'Cancel',
                variant: 'outline',
                className: 'primary',
                onClick: closeRevokeConfirm,
                disabled: !!revokingId,
              },
              {
                label: revokingId ? 'Revoking...' : 'Revoke',
                variant: 'primary',
                className: 'white bg-blue600',
                onClick: handleRevokeConfirm,
                disabled: !!revokingId,
                loading: !!revokingId,
              },
            ]}
            align="right"
          />
        }
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {tokenToRevoke
              ? `Revoke token "${tokenToRevoke.name}"? This cannot be undone and any clients using this token will stop working.`
              : 'Revoke this token?'}
          </Typography>
        </Box>
      </ModalWrapper>

      <ModalWrapper
        isOpen={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        title="Delete API token"
        size="small"
        closeOnBackdrop={false}
        footer={
          <CommonFooter
            buttons={[
              {
                label: 'Cancel',
                variant: 'outline',
                className: 'primary',
                onClick: closeDeleteConfirm,
                disabled: !!deletingId,
              },
              {
                label: deletingId ? 'Deleting...' : 'Delete',
                variant: 'primary',
                className: 'white bg-blue600',
                onClick: handleDeleteConfirm,
                disabled: !!deletingId,
                loading: !!deletingId,
              },
            ]}
            align="right"
          />
        }
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {tokenToDelete
              ? `Permanently delete token "${tokenToDelete.name}"? It will be removed from your token list and cannot be recovered.`
              : 'Delete this token?'}
          </Typography>
        </Box>
      </ModalWrapper>
    </Box>
  );
};

export default ApiTokensTab;
